// App.tsx - Componente principal con rutas completas
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTenant } from './contexts/TenantContext';
import { AuthProvider } from './shared/hooks/useAuth';
import { WebSocketProvider } from './contexts/WebSocketContext';
import ProtectedRoute from './shared/components/ProtectedRoute';
import SuperAdminRoute from './shared/components/SuperAdminRoute';

// Páginas públicas
import LandingPage from './pages/public/LandingPage';
const RegisterPage = React.lazy(() => import('./pages/public/RegisterPage'));
const OnboardingWizard = React.lazy(() => import('./pages/public/OnboardingWizard'));
const ClientRegisterPage = React.lazy(() => import('./pages/public/ClientRegisterPage'));
const PublicCalculatorPage = React.lazy(() => import('./pages/public/PublicCalculatorPage'));
const TrackingPage = React.lazy(() => import('./pages/public/TrackingPage'));
const PrivacyPage = React.lazy(() => import('./pages/public/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/public/TermsPage'));

// Páginas de Tenant
import LoginPage from './pages/LoginPage';
const DashboardPage = React.lazy(() => import('./pages/DashboardPageWithCharts'));
const PackagesPage = React.lazy(() => import('./pages/PackagesPage'));
const EnhancedNewPackagePage = React.lazy(() => import('./pages/EnhancedNewPackagePage'));
const PackageDetailPage = React.lazy(() => import('./pages/PackageDetailPage'));
const ClientsPage = React.lazy(() => import('./pages/ClientsPage'));
const UsersPage = React.lazy(() => import('./pages/UsersPage'));
const CouriersPage = React.lazy(() => import('./pages/CouriersPage'));
const RatesPage = React.lazy(() => import('./pages/RatesPage'));
const RateCalculator = React.lazy(() => import('./pages/RateCalculator'));
const IncidentsPage = React.lazy(() => import('./pages/IncidentsPage'));
const FuelPage = React.lazy(() => import('./pages/FuelPage'));
const VehiclesPage = React.lazy(() => import('./pages/VehiclesPage'));
const MaintenancePage = React.lazy(() => import('./pages/MaintenancePage'));
const DocumentsPage = React.lazy(() => import('./pages/DocumentsPage'));
const WarehousePage = React.lazy(() => import('./pages/WarehousePage'));
const ArrivalsPage = React.lazy(() => import('./pages/ArrivalsPage'));
const ManifestsPage = React.lazy(() => import('./pages/ManifestsPage'));
const ShippersPage = React.lazy(() => import('./pages/ShippersPage'));
const ExpensesPage = React.lazy(() => import('./pages/ExpensesPage'));
const SuppliersPage = React.lazy(() => import('./pages/SuppliersPage'));
import TenantLoadingPage from './pages/TenantLoadingPage';
import TenantErrorPage from './pages/TenantErrorPage';

// Páginas de Fase 1 (Críticas)
const GuidesPage = React.lazy(() => import('./pages/GuidesPage'));
const LabelsPage = React.lazy(() => import('./pages/LabelsPage'));

// Páginas de Fase 2 (Críticas)
const DeliveryNotesPage = React.lazy(() => import('./pages/DeliveryNotesPage'));
const DeclarationsPage = React.lazy(() => import('./pages/DeclarationsPage'));
const ConsolidationsPage = React.lazy(() => import('./pages/ConsolidationsPage'));
const FiscalWarehousePage = React.lazy(() => import('./pages/FiscalWarehousePage'));
const ShipmentsPage = React.lazy(() => import('./pages/ShipmentsPage'));
const WarehouseReceiptsPage = React.lazy(() => import('./pages/WarehouseReceiptsPage'));
const PackageIncidentsPage = React.lazy(() => import('./pages/PackageIncidentsPage'));
const PreAlertsPage = React.lazy(() => import('./pages/PreAlertsPage'));
const ClientPreAlertsPage = React.lazy(() => import('./pages/client/ClientPreAlertsPage'));
const MailboxesPage = React.lazy(() => import('./pages/MailboxesPage'));
const ClientMailboxPage = React.lazy(() => import('./pages/client/ClientMailboxPage'));
const ClientQuotesPage = React.lazy(() => import('./pages/client/ClientQuotesPage'));
const QuotesPage = React.lazy(() => import('./pages/QuotesPage'));
const AdditionalServicesPage = React.lazy(() => import('./pages/AdditionalServicesPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));

// Páginas del Portal de Agentes
const AgentDashboard = React.lazy(() => import('./pages/agent/AgentDashboard'));
const AgentClients = React.lazy(() => import('./pages/agent/AgentClients'));
const AgentCommissions = React.lazy(() => import('./pages/agent/AgentCommissions'));
const AgentCRM = React.lazy(() => import('./pages/agent/AgentCRM'));
const AgentQuotes = React.lazy(() => import('./pages/agent/AgentQuotes'));
const AgentReports = React.lazy(() => import('./pages/agent/AgentReports'));

// Páginas del Portal de Sucursal
const BranchAgentDashboard = React.lazy(() => import('./pages/branch/BranchAgentDashboard'));
const BranchInventory = React.lazy(() => import('./pages/branch/BranchInventory'));
const BranchTransfers = React.lazy(() => import('./pages/branch/BranchTransfers'));
const BranchCashControl = React.lazy(() => import('./pages/branch/BranchCashControl'));

// Páginas del Portal de Couriers
const CourierDashboard = React.lazy(() => import('./pages/courier/CourierDashboard'));
const CourierDeliveriesPage = React.lazy(() => import('./pages/courier/CourierDeliveriesPage'));
const CourierEarnings = React.lazy(() => import('./pages/courier/CourierEarnings'));
const CourierScanner = React.lazy(() => import('./pages/courier/CourierScanner'));
const CourierPerformance = React.lazy(() => import('./pages/courier/CourierPerformance'));

// Páginas del Portal de Clientes
const ClientDashboard = React.lazy(() => import('./pages/client/ClientDashboard'));
const ClientPackages = React.lazy(() => import('./pages/client/ClientPackages'));
const ClientPackageDetail = React.lazy(() => import('./pages/client/ClientPackageDetail'));
const ClientProfile = React.lazy(() => import('./pages/client/ClientProfile'));
const ClientBilling = React.lazy(() => import('./pages/client/ClientBilling'));

// Páginas de Account
const WhiteLabelSettings = React.lazy(() => import('./pages/account/WhiteLabelSettings'));
const BrandingSettingsPage = React.lazy(() => import('./pages/account/BrandingSettingsPage'));

// Páginas específicas por rol
const MyPackagesPage = React.lazy(() => import('./pages/MyPackagesPage'));
const MyDeliveriesPage = React.lazy(() => import('./pages/MyDeliveriesPage'));
const DispatchPage = React.lazy(() => import('./pages/DispatchPage'));
import RoleBasedRedirect from './components/RoleBasedRedirect';

// Páginas de Super Admin
const SuperAdminDashboard = React.lazy(() => import('./pages/super-admin/SuperAdminDashboard'));
const TenantsManagement = React.lazy(() => import('./pages/super-admin/TenantsManagement'));
const TenantDetail = React.lazy(() => import('./pages/super-admin/TenantDetail'));
const CreateTenantPage = React.lazy(() => import('./pages/super-admin/CreateTenantPage'));
const BillingPage = React.lazy(() => import('./pages/super-admin/BillingPage'));
const AnalyticsPage = React.lazy(() => import('./pages/super-admin/AnalyticsPage'));
const SettingsPage = React.lazy(() => import('./pages/super-admin/SettingsPage'));
const CourierTrackingPage = React.lazy(() => import('./pages/admin/CourierTrackingPage'));
const AdminPaymentsPage = React.lazy(() => import('./pages/admin/AdminPaymentsPage'));

const App: React.FC = () => {
    const { tenant: _tenant, loading, error } = useTenant();
    const location = useLocation();

    // Rutas que no requieren tenant (públicas del SaaS principal)
    const publicPaths = ['/', '/register', '/onboarding', '/pricing', '/about', '/privacy', '/terms'];
    const isPublicPath = publicPaths.some(p => location.pathname === p || location.pathname.startsWith('/onboarding'));

    // En el dominio principal, mostrar landing/registro
    const isMainDomain = window.location.hostname === 'sistemacourier.com' ||
        window.location.hostname === 'www.sistemacourier.com' ||
        window.location.hostname === 'localhost';

    // Si es ruta pública en dominio principal, no necesita tenant
    if (isMainDomain && isPublicPath) {
        return (
            <div className="app">
                <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen text-gray-500">Cargando…</div>}>
<Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/onboarding" element={<OnboardingWizard />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
</React.Suspense>
            </div>
        );
    }

    // Mostrar loading mientras se resuelve el tenant
    if (loading) {
        return <TenantLoadingPage />;
    }

    // Mostrar error si no se pudo resolver el tenant
    if (error && !isMainDomain) {
        return <TenantErrorPage message={error} />;
    }

    return (
        <AuthProvider>
            <WebSocketProvider>
                <div className="app">
                    <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen text-gray-500">Cargando…</div>}>
<Routes>
                        {/* Rutas públicas */}
                        <Route path="/client/register" element={<ClientRegisterPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/calculator" element={<PublicCalculatorPage />} />
                        <Route path="/track" element={<TrackingPage />} />
                        <Route path="/track/:tracking" element={<TrackingPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/terms" element={<TermsPage />} />

                        {/* Rutas protegidas - requieren autenticación */}
                        <Route path="/dashboard" element={
                            <ProtectedRoute>
                                <DashboardPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/packages" element={
                            <ProtectedRoute>
                                <PackagesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/packages/new" element={
                            <ProtectedRoute>
                                <EnhancedNewPackagePage />
                            </ProtectedRoute>
                        } />

                        <Route path="/packages/:id" element={
                            <ProtectedRoute>
                                <PackageDetailPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/clients" element={
                            <ProtectedRoute>
                                <ClientsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/users" element={
                            <ProtectedRoute>
                                <UsersPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/couriers" element={
                            <ProtectedRoute>
                                <CouriersPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/couriers/tracking" element={
                            <ProtectedRoute>
                                <CourierTrackingPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/admin/payments" element={
                            <ProtectedRoute>
                                <AdminPaymentsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/rates" element={
                            <ProtectedRoute>
                                <RatesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/rate-calculator" element={
                            <ProtectedRoute>
                                <RateCalculator />
                            </ProtectedRoute>
                        } />

                        <Route path="/incidents" element={
                            <ProtectedRoute>
                                <IncidentsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/fuel" element={
                            <ProtectedRoute>
                                <FuelPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/vehicles" element={
                            <ProtectedRoute>
                                <VehiclesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/maintenance" element={
                            <ProtectedRoute>
                                <MaintenancePage />
                            </ProtectedRoute>
                        } />

                        <Route path="/documents" element={
                            <ProtectedRoute>
                                <DocumentsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/warehouse" element={
                            <ProtectedRoute>
                                <WarehousePage />
                            </ProtectedRoute>
                        } />

                        <Route path="/arrivals" element={
                            <ProtectedRoute>
                                <ArrivalsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/manifests" element={
                            <ProtectedRoute>
                                <ManifestsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/dispatch" element={
                            <ProtectedRoute>
                                <DispatchPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/shippers" element={
                            <ProtectedRoute>
                                <ShippersPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/expenses" element={
                            <ProtectedRoute>
                                <ExpensesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/suppliers" element={
                            <ProtectedRoute>
                                <SuppliersPage />
                            </ProtectedRoute>
                        } />

                        {/* Fase 1: Módulos Críticos */}
                        <Route path="/guides" element={
                            <ProtectedRoute>
                                <GuidesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/labels" element={
                            <ProtectedRoute>
                                <LabelsPage />
                            </ProtectedRoute>
                        } />

                        {/* Fase 2: Módulos Críticos */}
                        <Route path="/delivery-notes" element={
                            <ProtectedRoute>
                                <DeliveryNotesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/customs-declarations" element={
                            <ProtectedRoute>
                                <DeclarationsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/shipments" element={
                            <ProtectedRoute>
                                <ShipmentsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/consolidations" element={
                            <ProtectedRoute>
                                <ConsolidationsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/fiscal-warehouse" element={
                            <ProtectedRoute>
                                <FiscalWarehousePage />
                            </ProtectedRoute>
                        } />

                        <Route path="/warehouse-receipts" element={
                            <ProtectedRoute>
                                <WarehouseReceiptsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/package-incidents" element={
                            <ProtectedRoute>
                                <PackageIncidentsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/pre-alerts" element={
                            <ProtectedRoute>
                                <PreAlertsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/mailboxes" element={
                            <ProtectedRoute>
                                <MailboxesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/quotes" element={
                            <ProtectedRoute>
                                <QuotesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/additional-services" element={
                            <ProtectedRoute>
                                <AdditionalServicesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/reports" element={
                            <ProtectedRoute>
                                <ReportsPage />
                            </ProtectedRoute>
                        } />

                        {/* Rutas específicas por rol */}
                        <Route path="/my-packages" element={
                            <ProtectedRoute>
                                <MyPackagesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/my-deliveries" element={
                            <ProtectedRoute>
                                <MyDeliveriesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/client/pre-alerts" element={
                            <ProtectedRoute>
                                <ClientPreAlertsPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/client/mailbox" element={
                            <ProtectedRoute>
                                <ClientMailboxPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/client/quotes" element={
                            <ProtectedRoute>
                                <ClientQuotesPage />
                            </ProtectedRoute>
                        } />

                        {/* Rutas del Portal de Agentes */}
                        <Route path="/agent/dashboard" element={
                            <ProtectedRoute>
                                <AgentDashboard />
                            </ProtectedRoute>
                        } />

                        <Route path="/agent/clients" element={
                            <ProtectedRoute>
                                <AgentClients />
                            </ProtectedRoute>
                        } />

                        <Route path="/agent/commissions" element={
                            <ProtectedRoute>
                                <AgentCommissions />
                            </ProtectedRoute>
                        } />

                        <Route path="/agent/crm" element={
                            <ProtectedRoute>
                                <AgentCRM />
                            </ProtectedRoute>
                        } />

                        <Route path="/agent/quotes" element={
                            <ProtectedRoute>
                                <AgentQuotes />
                            </ProtectedRoute>
                        } />

                        <Route path="/agent/reports" element={
                            <ProtectedRoute>
                                <AgentReports />
                            </ProtectedRoute>
                        } />

                        {/* Rutas del Portal de Sucursal */}
                        <Route path="/branch/dashboard" element={
                            <ProtectedRoute>
                                <BranchAgentDashboard />
                            </ProtectedRoute>
                        } />

                        <Route path="/branch/inventory" element={
                            <ProtectedRoute>
                                <BranchInventory />
                            </ProtectedRoute>
                        } />

                        <Route path="/branch/transfers" element={
                            <ProtectedRoute>
                                <BranchTransfers />
                            </ProtectedRoute>
                        } />

                        <Route path="/branch/cash" element={
                            <ProtectedRoute>
                                <BranchCashControl />
                            </ProtectedRoute>
                        } />

                        {/* Rutas del Portal de Couriers */}
                        <Route path="/courier/dashboard" element={
                            <ProtectedRoute>
                                <CourierDashboard />
                            </ProtectedRoute>
                        } />

                        <Route path="/courier/deliveries" element={
                            <ProtectedRoute>
                                <CourierDeliveriesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/courier/earnings" element={
                            <ProtectedRoute>
                                <CourierEarnings />
                            </ProtectedRoute>
                        } />

                        <Route path="/courier/scanner" element={
                            <ProtectedRoute>
                                <CourierScanner />
                            </ProtectedRoute>
                        } />

                        <Route path="/courier/performance" element={
                            <ProtectedRoute>
                                <CourierPerformance />
                            </ProtectedRoute>
                        } />

                        {/* Rutas del Portal de Clientes */}
                        <Route path="/client/dashboard" element={
                            <ProtectedRoute>
                                <ClientDashboard />
                            </ProtectedRoute>
                        } />

                        <Route path="/client/packages/:id" element={
                            <ProtectedRoute>
                                <ClientPackageDetail />
                            </ProtectedRoute>
                        } />

                        <Route path="/client/packages" element={
                            <ProtectedRoute>
                                <ClientPackages />
                            </ProtectedRoute>
                        } />

                        <Route path="/client/quotes" element={
                            <ProtectedRoute>
                                <ClientQuotesPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/client/profile" element={
                            <ProtectedRoute>
                                <ClientProfile />
                            </ProtectedRoute>
                        } />

                        <Route path="/client/billing" element={
                            <ProtectedRoute>
                                <ClientBilling />
                            </ProtectedRoute>
                        } />

                        {/* Account Settings */}
                        <Route path="/account/white-label" element={
                            <ProtectedRoute>
                                <WhiteLabelSettings />
                            </ProtectedRoute>
                        } />
                        <Route path="/account/branding" element={
                            <ProtectedRoute>
                                <BrandingSettingsPage />
                            </ProtectedRoute>
                        } />

                        {/* Super Admin (solo para administradores del SaaS) */}
                        <Route path="/super-admin" element={
                            <SuperAdminRoute>
                                <SuperAdminDashboard />
                            </SuperAdminRoute>
                        } />
                        <Route path="/super-admin/tenants" element={
                            <SuperAdminRoute>
                                <TenantsManagement />
                            </SuperAdminRoute>
                        } />
                        <Route path="/super-admin/tenants/new" element={
                            <SuperAdminRoute>
                                <CreateTenantPage />
                            </SuperAdminRoute>
                        } />
                        <Route path="/super-admin/tenants/:id" element={
                            <SuperAdminRoute>
                                <TenantDetail />
                            </SuperAdminRoute>
                        } />
                        <Route path="/billing" element={
                            <SuperAdminRoute>
                                <BillingPage />
                            </SuperAdminRoute>
                        } />
                        <Route path="/analytics" element={
                            <SuperAdminRoute>
                                <AnalyticsPage />
                            </SuperAdminRoute>
                        } />
                        <Route path="/settings" element={
                            <SuperAdminRoute>
                                <SettingsPage />
                            </SuperAdminRoute>
                        } />

                        {/* Redirección por defecto basada en rol */}
                        <Route path="/" element={
                            <ProtectedRoute>
                                <RoleBasedRedirect />
                            </ProtectedRoute>
                        } />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
</React.Suspense>
                </div>
            </WebSocketProvider>
        </AuthProvider>
    );
};

export default App;
